import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Package, MapPin, Calendar } from 'lucide-react';
import { NumericInput } from '../../../../common/inputs';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface MandatoryCountingModalProps {
  isOpen: boolean;
  itemNo: string;
  itemDescription: string;
  binCode: string;
  lastCountedDate?: string | null;
  daysSinceLastCounted?: number | null;
  currentQuantity: number;
  onSubmit: (countedQuantity: number) => void;
  isSubmitting?: boolean;
  // Valgfri gjenbruk som frivillig telle-snarvei (strukturplukk): egne tekster + avbryt-knapp.
  // Uten disse beholdes obligatorisk-telling-oppførselen uendret (ingen avbryt).
  title?: string;
  subtitle?: string;
  confirmLabel?: string;
  onCancel?: () => void;
}

export const MandatoryCountingModal: React.FC<MandatoryCountingModalProps> = ({
  isOpen,
  itemNo,
  itemDescription,
  binCode,
  lastCountedDate,
  daysSinceLastCounted,
  currentQuantity,
  onSubmit,
  isSubmitting = false,
  title,
  subtitle,
  confirmLabel,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [countedQuantity, setCountedQuantity] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setCountedQuantity('');
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSubmit = () => {
    const quantity = parseFloat(countedQuantity);
    if (isNaN(quantity) || quantity < 0) {
      alert(t('warehouse.modules.picking.mandatoryCounting.alertInvalidQuantity'));
      return;
    }
    onSubmit(quantity);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-md overflow-y-auto">
        {/* Header - Mandatory Counting Required */}
        <div className="bg-yellow-500 text-white px-4 py-4 sm:px-6 sm:rounded-t-lg sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <AlertCircle size={28} />
            <div>
              <h2 className="text-xl font-bold">{title ?? t('warehouse.modules.picking.mandatoryCounting.title')}</h2>
              <p className="text-sm text-yellow-100">
                {subtitle ?? t('warehouse.modules.picking.mandatoryCounting.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4">
          {/* Info Section */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-2">
            <div className="flex items-start gap-2">
              <Package size={18} className="text-yellow-700 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-900">{itemNo}</p>
                <p className="text-xs text-yellow-700">{itemDescription}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-yellow-700" />
              <p className="text-sm text-yellow-900">
                <span className="font-semibold">{t('warehouse.general.bin')}:</span> {binCode}
              </p>
            </div>

            {lastCountedDate ? (
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-yellow-700" />
                <p className="text-sm text-yellow-900">
                  <span className="font-semibold">{t('warehouse.modules.picking.mandatoryCounting.lastCounted')}:</span>{' '}
                  {new Date(lastCountedDate).toLocaleDateString('no-NO')}
                  {daysSinceLastCounted && (
                    <span className="text-yellow-700"> {t('warehouse.modules.picking.mandatoryCounting.daysAgo', { 0: daysSinceLastCounted })}</span>
                  )}
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-yellow-700" />
                <p className="text-sm text-yellow-900">
                  <span className="font-semibold">{t('warehouse.modules.picking.mandatoryCounting.lastCounted')}:</span> {t('warehouse.modules.picking.mandatoryCounting.neverCounted')}
                </p>
              </div>
            )}

            <div className="pt-2 border-t border-yellow-200">
              <p className="text-xs text-yellow-800">
                {t('warehouse.modules.picking.mandatoryCounting.registeredStock')}: <span className="font-semibold">{currentQuantity}</span>
              </p>
            </div>
          </div>

          {/* Counting Input */}
          <div>
            <label htmlFor="countedQuantity" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('warehouse.modules.picking.mandatoryCounting.countLabel')}
            </label>
            <NumericInput
              ref={inputRef}
              id="countedQuantity"
              mode="decimal"
              min="0"
              value={countedQuantity}
              onChange={(e) => setCountedQuantity(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSubmitting}
              className="w-full px-4 py-3 min-h-[44px] text-2xl font-bold text-center border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
            />
            <p className="mt-2 text-xs text-gray-600">
              {t('warehouse.modules.picking.mandatoryCounting.countHint')}
            </p>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !countedQuantity}
            className="w-full px-6 py-4 min-h-[44px] bg-blue-600 text-white text-lg font-bold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting
              ? t('warehouse.modules.picking.mandatoryCounting.registeringCount')
              : (confirmLabel ?? t('warehouse.modules.picking.mandatoryCounting.okContinuePicking'))}
          </button>

          {onCancel ? (
            /* Frivillig telling (strukturplukk-snarvei): kan avbrytes */
            <button
              onClick={onCancel}
              disabled={isSubmitting}
              className="w-full px-6 py-3 min-h-[44px] bg-gray-100 text-gray-700 text-base font-semibold rounded-lg hover:bg-gray-200 disabled:cursor-not-allowed transition-colors"
            >
              {t('warehouse.general.cancel', {}, 'Avbryt')}
            </button>
          ) : (
            /* Obligatorisk telling: kan ikke avbrytes */
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs text-red-800 text-center">
                <strong>{t('warehouse.modules.picking.mandatoryCounting.noteLabel')}:</strong> {t('warehouse.modules.picking.mandatoryCounting.cannotCancelWarning')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
